import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Search, AlertTriangle, GitMerge, CheckCircle2, Loader2, Users, Phone, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const fmtAED = (v) => `AED ${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;

export default function DuplicateManagerPage() {
    const [scanResult, setScanResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mergeDialog, setMergeDialog] = useState(null);
    const [primaryId, setPrimaryId] = useState('');
    const [merging, setMerging] = useState(false);
    const [mergeRequests, setMergeRequests] = useState([]);
    const [tab, setTab] = useState('scan');

    const runScan = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiClient.get('/students/duplicates/scan');
            setScanResult(res.data);
        } catch (err) {
            toast.error('Failed to scan for duplicates');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchMergeRequests = useCallback(async () => {
        try {
            const res = await apiClient.get('/students/merge/requests');
            setMergeRequests(res.data);
        } catch { }
    }, []);

    useEffect(() => { runScan(); fetchMergeRequests(); }, [runScan, fetchMergeRequests]);

    const openMergeDialog = (group) => {
        setPrimaryId(group.suggested_primary_id);
        setMergeDialog(group);
    };

    const submitMerge = async () => {
        if (!mergeDialog || !primaryId) return;
        setMerging(true);
        try {
            const secondaryIds = mergeDialog.students.filter(s => s.id !== primaryId).map(s => s.id);
            await apiClient.post('/students/merge/request', {
                primary_id: primaryId,
                secondary_ids: secondaryIds,
                reason: `Duplicate detected by scanner (${mergeDialog.match_type}: ${mergeDialog.match_value})`,
            });
            toast.success('Merge request submitted for approval');
            setMergeDialog(null);
            runScan();
            fetchMergeRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to submit merge request');
        } finally {
            setMerging(false);
        }
    };

    const handleApprove = async (reqId) => {
        try {
            await apiClient.post(`/students/merge/${reqId}/approve`);
            toast.success('Merge approved');
            fetchMergeRequests();
            runScan();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to approve');
        }
    };

    const handleReject = async (reqId) => {
        try {
            await apiClient.post(`/students/merge/${reqId}/reject`);
            toast.success('Merge rejected');
            fetchMergeRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to reject');
        }
    };

    return (
        <div className="space-y-6" data-testid="duplicate-manager-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Duplicate Manager</h1>
                    <p className="text-muted-foreground text-sm">Detect and merge duplicate student records</p>
                </div>
                <Button onClick={runScan} disabled={loading} variant="outline" data-testid="scan-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    {loading ? 'Scanning...' : 'Re-scan'}
                </Button>
            </div>

            {/* Summary Cards */}
            {scanResult && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="pt-4 flex items-center gap-3">
                            <Users className="h-8 w-8 text-blue-500" />
                            <div>
                                <p className="text-2xl font-bold" data-testid="total-students">{scanResult.total_students}</p>
                                <p className="text-xs text-muted-foreground">Total Students</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4 flex items-center gap-3">
                            <AlertTriangle className="h-8 w-8 text-amber-500" />
                            <div>
                                <p className="text-2xl font-bold" data-testid="duplicate-groups">{scanResult.duplicate_groups}</p>
                                <p className="text-xs text-muted-foreground">Duplicate Groups</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4 flex items-center gap-3">
                            <GitMerge className="h-8 w-8 text-red-500" />
                            <div>
                                <p className="text-2xl font-bold" data-testid="total-duplicates">{scanResult.total_duplicates}</p>
                                <p className="text-xs text-muted-foreground">Duplicate Records</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-2 border-b">
                <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'scan' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setTab('scan')}
                    data-testid="tab-scan"
                >
                    Detected Duplicates ({scanResult?.duplicate_groups || 0})
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'requests' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setTab('requests')}
                    data-testid="tab-requests"
                >
                    Merge Requests ({mergeRequests.length})
                </button>
            </div>

            {/* Scan Results Tab */}
            {tab === 'scan' && scanResult && (
                <div className="space-y-4">
                    {scanResult.groups.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                                <p className="text-lg font-medium">No duplicates found</p>
                                <p className="text-sm text-muted-foreground">All student records are unique</p>
                            </CardContent>
                        </Card>
                    ) : (
                        scanResult.groups.map((group, gi) => (
                            <Card key={gi} data-testid={`duplicate-group-${gi}`}>
                                <CardHeader className="pb-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            {group.match_type === 'phone' ? <Phone className="h-4 w-4 text-blue-500" /> : <Mail className="h-4 w-4 text-purple-500" />}
                                            <CardTitle className="text-sm">
                                                {group.match_type === 'phone' ? 'Phone' : 'Email'}: {group.match_value}
                                            </CardTitle>
                                            <Badge variant="destructive" className="text-xs">{group.count} records</Badge>
                                        </div>
                                        <Button size="sm" onClick={() => openMergeDialog(group)} data-testid={`merge-btn-${gi}`}>
                                            <GitMerge className="h-4 w-4 mr-1" /> Merge
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Phone</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Course</TableHead>
                                                <TableHead>CS Agent</TableHead>
                                                <TableHead>Mentor</TableHead>
                                                <TableHead>Amount</TableHead>
                                                <TableHead>Created</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {group.students.map((s, si) => (
                                                <TableRow key={s.id} className={si === 0 ? 'bg-emerald-500/5' : ''}>
                                                    <TableCell className="font-medium text-sm">
                                                        {s.full_name}
                                                        {si === 0 && <Badge variant="outline" className="ml-1 text-[10px]">Primary</Badge>}
                                                    </TableCell>
                                                    <TableCell className="text-xs">{s.phone || '-'}</TableCell>
                                                    <TableCell className="text-xs">{s.email || '-'}</TableCell>
                                                    <TableCell className="text-xs">{s.current_course_name || '-'}</TableCell>
                                                    <TableCell className="text-xs">{s.cs_agent_name || '-'}</TableCell>
                                                    <TableCell className="text-xs">{s.mentor_name || '-'}</TableCell>
                                                    <TableCell className="text-xs font-mono">{fmtAED(s.enrollment_amount)}</TableCell>
                                                    <TableCell className="text-xs">{fmtDate(s.created_at)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            )}

            {/* Merge Requests Tab */}
            {tab === 'requests' && (
                <div className="space-y-3">
                    {mergeRequests.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center text-muted-foreground">No merge requests yet</CardContent>
                        </Card>
                    ) : (
                        mergeRequests.map((req, i) => (
                            <Card key={req.id} data-testid={`merge-request-${i}`}>
                                <CardContent className="pt-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium text-sm">
                                                Merge {req.secondary_ids?.length || 0} record(s) into {req.primary_name || req.primary_id}
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">{req.reason}</p>
                                            <p className="text-xs text-muted-foreground">Requested {fmtDate(req.created_at)}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant={
                                                req.status === 'approved' ? 'default' :
                                                req.status === 'rejected' ? 'destructive' :
                                                'secondary'
                                            }>
                                                {req.status?.replace('_', ' ')}
                                            </Badge>
                                            {(req.status === 'pending_cs_head' || req.status === 'pending_ceo') && (
                                                <>
                                                    <Button size="sm" variant="default" onClick={() => handleApprove(req.id)} data-testid={`approve-merge-${i}`}>
                                                        Approve
                                                    </Button>
                                                    <Button size="sm" variant="destructive" onClick={() => handleReject(req.id)} data-testid={`reject-merge-${i}`}>
                                                        Reject
                                                    </Button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            )}

            {/* Merge Dialog */}
            <Dialog open={!!mergeDialog} onOpenChange={(open) => !open && setMergeDialog(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <GitMerge className="h-5 w-5" /> Select Primary Record
                        </DialogTitle>
                        <DialogDescription>
                            Choose which record to keep. All transactions from other records will be merged into the primary.
                        </DialogDescription>
                    </DialogHeader>
                    {mergeDialog && (
                        <RadioGroup value={primaryId} onValueChange={setPrimaryId} className="space-y-2">
                            {mergeDialog.students.map((s) => (
                                <div key={s.id} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${primaryId === s.id ? 'border-primary bg-primary/5' : 'border-border'}`}>
                                    <RadioGroupItem value={s.id} id={s.id} />
                                    <Label htmlFor={s.id} className="flex-1 cursor-pointer">
                                        <p className="text-sm font-medium">{s.full_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {s.phone} | {s.email || 'No email'} | {s.current_course_name || 'No course'} | {fmtDate(s.updated_at)}
                                        </p>
                                    </Label>
                                </div>
                            ))}
                        </RadioGroup>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMergeDialog(null)}>Cancel</Button>
                        <Button onClick={submitMerge} disabled={merging || !primaryId}>
                            {merging ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GitMerge className="h-4 w-4 mr-2" />}
                            Submit Merge Request
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
